import { FC } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { GridCloseIcon } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";

import DocumentManagementForm from "../../Document-Management/form/DocumentManagementForm";
import { GENRIC_ATTACHMENT_VIEW_URL } from "../../../Common/EndPoints";
import { userIdSelector } from "../../../features/authentication/auth-slice";
import {
  DocumentFieldType,
  DocumentFormFieldType,
  SingleDocument,
} from "../../../types/document-management/document-management-types";
import axios from "axios";
import { downloadFile } from "../../../Utils/Utils";
import document_management_apis, { DeleteDocumentAttachmentParams } from "../../../Services/document-management-apis";

function getFileBlob(path: string) {
  return axios.get(path, { responseType: "blob" });
}
interface DocumentAttachmentProps {
  name: string;
  path: string;
  index: number;
  document: SingleDocument;
}
const DocumentAttachment: FC<DocumentAttachmentProps> = ({ name, path, index, document }) => {
  const [deleteAttachment, { isLoading }] = document_management_apis.useDeleteDocumentAttachmentMutation();
  const user_id = useSelector(userIdSelector);

  async function handleDeleteAttachment() {
    const params: DeleteDocumentAttachmentParams = {
      id: document.id,
      attachment_path: path,
      user_id,
    };

    try {
      await deleteAttachment(params).unwrap();
    } catch (error) {
      toast.error("Error deleting attachment");
    }
  }

  async function handleAttachmentDownload(path: string) {
    const loading_toast_id = toast.loading("Downloading");
    try {
      const responseBlob = await getFileBlob(path);
      const data = responseBlob.data;
      downloadFile({ name, data });
    } catch (error: any) {
      // console.error("ATTACHMENT_DOWNLOAD", error);
      if (error.message) {
        toast.error(error.message);
      }
    } finally {
      toast.dismiss(loading_toast_id);
    }
  }

  const ATTACHMENT_URL = GENRIC_ATTACHMENT_VIEW_URL + `/api/v2/${path}`;
  return (
    <div className="flex items-center gap-4">
      <div className="flex gap-1">
        <p>{index}.</p>
        <a href={ATTACHMENT_URL} rel="noreferrer" target="_blank">
          {name}
        </a>
      </div>
      <div className="flex items-center">
        <IconButton className="pointer" onClick={() => handleAttachmentDownload(ATTACHMENT_URL)}>
          <DownloadIcon />
        </IconButton>
        {isLoading ? (
          <div className="lds-ring lds-ring--black">
            <div></div>
          </div>
        ) : (
          <IconButton className="pointer" onClick={handleDeleteAttachment}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
};

const DEFAULT_FILTER_VALUES = { files: [] };

function getFileNameFromPath(path: string) {
  // console.log("path", path);
  const _split_path = path.split("/");
  const _file_name = _split_path[_split_path.length - 1];
  const _split_name = _file_name.split("_");
  const [_, ...name] = _split_name;
  return name.join("_");
}
interface DocumentAttachmentsModalProps {
  handleClose: () => void;
  defaultValues: SingleDocument;
}

const DocumentAttachmentsModal: FC<DocumentAttachmentsModalProps> = ({ handleClose, defaultValues }) => {
  const files = defaultValues.attachment_paths || [];
  const fields: DocumentFormFieldType[] = [{ type: DocumentFieldType.Attachment, id: "files", required: true }];

  const [uploadAttachments, { isLoading }] = document_management_apis.useUploadDocumentAttachmentMutation();
  const user_id = useSelector(userIdSelector);

  type AttachmentFormValues = { files: File[] };

  async function handleAttachmentUpload(params: AttachmentFormValues) {
    const { files } = params;
    const form_data = new FormData();
    files.map((file) => form_data.append("files", file));
    const _attachmentParams = {
      id: defaultValues.id,
      user_id,
      files: form_data,
    };
    try {
      await uploadAttachments(_attachmentParams).unwrap();
      toast.success("Uploaded Successfully");
    } catch (error) {
      // console.error("UPLOAD_DOCUMENT_ATTACHMENTS_MODAL", error);
      toast.error("Error Uploading Attachments With Document");
    } finally {
      handleClose();
    }
  }

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        Attachments
        <GridCloseIcon className="assets-info-popup-close-icon pointer" onClick={handleClose} />
      </DialogTitle>
      <DialogContent>
        <div>
          {files?.map((file, index) => (
            <DocumentAttachment
              path={file}
              key={defaultValues.id + (index + 1)}
              name={getFileNameFromPath(file)}
              index={index + 1}
              document={defaultValues}
            />
          ))}
        </div>
        <DocumentManagementForm
          defaultValues={DEFAULT_FILTER_VALUES}
          formFields={fields}
          handleFormSubmit={handleAttachmentUpload}
          className="upload-documents-form flex gap-4 items-center flex-wrap p-2"
          loading={isLoading}
          confirmButtonText="Upload"
        />
      </DialogContent>
    </Dialog>
  );
};

export default DocumentAttachmentsModal;
